import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, View, TouchableOpacity, Text, Dimensions, Platform, StyleSheet } from 'react-native'
import Slider from '@react-native-community/slider'
import { Feather, Ionicons } from '@expo/vector-icons'
import VideoPlayerAV from '../VideoPlayer/expo-av'
import { ImageWithZoom } from './ImageWIthZoom'
import mediaSingleton from '../../../utils/mediaSingleton'
import * as NavigationBar from 'expo-navigation-bar'

const { width: screenW, height: screenH } = Dimensions.get('window')

import AsyncStorage from '@react-native-async-storage/async-storage'

const toTime = (ms) => {
    const s = Math.floor((ms || 0) / 1000)
    const m = Math.floor(s / 60)
    const ss = s % 60
    return `${m}:${ss < 10 ? '0' : ''}${ss}`
}

export default function LearningFullscreen({
    visible,
    medias,
    startIndex = 0,
    onClose = () => {},
    autoplay = true,
    countdownSeconds = 0,
    path = 'learning',
    resumeKey,
    initialPosition,
    initialIsPlaying,
}) {
    const [index, setIndex] = useState(startIndex || 0)
    const videoRef = useRef(null)
    const endHandledRef = useRef(false)
    const upNextActiveRef = useRef(false)
    const [upNextCountdown, updateUpNextCountdown] = useState(null)
    // Setter seguro para countdown; evita problemas si el bundler conserva símbolos antiguos
    // useEffect(() => {
    //     if (Platform.OS != 'android') return
    //     if (visible) {
    //         NavigationBar.setBehaviorAsync('sticky-immersive')
    //         NavigationBar.setVisibilityAsync('hidden')
    //     } else {
    //         NavigationBar.setVisibilityAsync('visible')
    //         NavigationBar.setBehaviorAsync('inset-touch')
    //     }

    //     return () => {
    //         // restaurar al salir
    //         console.log('Entre al return ==>')
    //         NavigationBar.setVisibilityAsync('visible')
    //         NavigationBar.setBehaviorAsync('inset-touch')
    //     }
    // }, [visible])
    const setUpNext = (valOrUpdater) => {
        try {
            if (typeof valOrUpdater === 'function') {
                updateUpNextCountdown((prev) => {
                    let nextVal = prev
                    try {
                        nextVal = valOrUpdater(prev)
                    } catch {}
                    return nextVal
                })
            } else {
                updateUpNextCountdown(valOrUpdater)
            }
        } catch {}
    }
    const upNextIntervalRef = useRef(null)

    useEffect(() => {
        if (!visible) return
        // Pausar todo al abrir el fullscreen propio
        try {
            mediaSingleton.pauseAllExcept(null)
        } catch {}
        endHandledRef.current = false
        upNextActiveRef.current = false
        setUpNext(null)
        // Posición inicial preferente desde inline (si viene), de lo contrario cargar reanudación si existe
        ;(async () => {
            try {
                if (typeof initialPosition === 'number') {
                    setTimeout(async () => {
                        try {
                            await videoRef.current?.setPositionAsync(initialPosition)
                        } catch {}
                    }, 250)
                    if (typeof initialIsPlaying === 'boolean') {
                        setTimeout(async () => {
                            try {
                                if (initialIsPlaying) await videoRef.current?.playAsync()
                                else await videoRef.current?.pauseAsync()
                            } catch {}
                        }, 300)
                    }
                } else if (resumeKey) {
                    const raw = await AsyncStorage.getItem(`learning_resume:${resumeKey}`)
                    if (raw) {
                        const data = JSON.parse(raw)
                        if (typeof data?.position === 'number') {
                            setTimeout(async () => {
                                try {
                                    await videoRef.current?.setPositionAsync(data.position)
                                } catch {}
                            }, 250)
                        }
                    }
                }
            } catch {}
        })()
    }, [visible])

    // Limpiar timers/estado cuando el modal se oculta o al desmontar
    useEffect(() => {
        if (!visible) {
            upNextActiveRef.current = false
            if (upNextIntervalRef.current) {
                clearInterval(upNextIntervalRef.current)
                upNextIntervalRef.current = null
            }
            setUpNext(null)
        }
        return () => {
            if (upNextIntervalRef.current) {
                clearInterval(upNextIntervalRef.current)
                upNextIntervalRef.current = null
            }
        }
    }, [visible])

    useEffect(() => {
        // Reset bandera de fin al cambiar de index
        endHandledRef.current = false
        // Reset overlay
        upNextActiveRef.current = false
        setUpNext(null)
        if (upNextIntervalRef.current) {
            clearInterval(upNextIntervalRef.current)
            upNextIntervalRef.current = null
        }
    }, [index])

    const hasPrev = index > 0
    const hasNext = index < (Array.isArray(medias) ? medias.length : 0) - 1

    const current = medias?.[index] || null
    const type = (current?.media_type || current?.content_type || '').toString().toLowerCase()
    const uri = current?.media_url || current?.media

    const instanceId = useMemo(() => `${path || 'learning'}:fs:${index}:${(uri || '').slice(-24)}`, [path, index, uri])

    const startUpNext = (seconds) => {
        if (upNextActiveRef.current) return
        if (!hasNext) return
        upNextActiveRef.current = true
        const initial = Math.max(1, Math.ceil(typeof seconds === 'number' ? seconds : 3))
        setUpNext(initial)
        if (upNextIntervalRef.current) clearInterval(upNextIntervalRef.current)
        upNextIntervalRef.current = setInterval(() => {
            setUpNext((prev) => {
                const next = (typeof prev === 'number' ? prev : initial) - 1
                if (next <= 0) {
                    clearInterval(upNextIntervalRef.current)
                    upNextIntervalRef.current = null
                    endHandledRef.current = true
                    setTimeout(() => setIndex((i) => i + 1), 50)
                    return 0
                }
                return next
            })
        }, 1000)
    }

    const cancelUpNext = () => {
        upNextActiveRef.current = false
        if (upNextIntervalRef.current) {
            clearInterval(upNextIntervalRef.current)
            upNextIntervalRef.current = null
        }
        setUpNext(null)
    }

    const [playback, setPlayback] = useState({ position: 0, duration: 0, isPlaying: false, volume: 1 })
    const progressBarRef = useRef(null)
    const volumeBarRef = useRef(null)
    const [hoverPct, setHoverPct] = useState(null)
    const [isSeeking, setIsSeeking] = useState(false)
    const [tempPosition, setTempPosition] = useState(null)
    const wasPlayingRef = useRef(false)
    const progressBarWidthRef = useRef(0)

    const handlePlayStatus = (status) => {
        try {
            if (status?.isLoaded) {
                const nextDuration = Math.max(0, status.durationMillis || 0)
                const reportedPos = Math.max(0, status.positionMillis || 0)
                const uiPos = isSeeking && typeof tempPosition === 'number' ? tempPosition : reportedPos
                setPlayback((prev) => ({
                    position: uiPos,
                    duration: nextDuration,
                    isPlaying: !!status.isPlaying,
                    volume: typeof status.volume === 'number' ? status.volume : prev?.volume ?? 1,
                }))
                // Guardar reanudación
                if (resumeKey) {
                    try {
                        AsyncStorage.setItem(`learning_resume:${resumeKey}`, JSON.stringify({ index, position: reportedPos }))
                    } catch {}
                }
            }
            if (status?.didJustFinish && !endHandledRef.current) {
                if (hasNext) {
                    // disparar overlay si no activo; countdown corto (1s)
                    if (!upNextActiveRef.current) startUpNext(1)
                } else {
                    endHandledRef.current = true
                    // Cerrar entregando payload para sincronizar inline (último elemento)
                    ;(async () => {
                        try {
                            const st = await videoRef.current?.getStatusAsync?.()
                            onClose({ index, position: Math.max(0, st?.positionMillis || 0), isPlaying: false })
                        } catch {
                            onClose({ index, position: playback.position || 0, isPlaying: false })
                        }
                    })()
                }
            } else if (status?.isLoaded && typeof status?.durationMillis === 'number' && typeof status?.positionMillis === 'number') {
                const remaining = Math.max(0, status.durationMillis - status.positionMillis)
                // Mostrar Up Next desde 5s antes si hay siguiente y no está activo
                if (hasNext && remaining <= 5000 && !upNextActiveRef.current) {
                    startUpNext(remaining / 1000)
                }
                // Rescue near-end si faltan <=300ms y no activó overlay por algún motivo
                if (remaining <= 300 && hasNext && !endHandledRef.current && !upNextActiveRef.current) {
                    startUpNext(1)
                }
            }
        } catch {}
    }

    if (!visible) return null

    const uiPosition = isSeeking && typeof tempPosition === 'number' ? tempPosition : playback.position
    const progressPct = playback.duration > 0 ? uiPosition / playback.duration : 0

    return (
        <Modal
            visible={visible}
            animationType='fade'
            transparent={false}
            onRequestClose={async () => {
                try {
                    const status = await videoRef.current?.getStatusAsync?.()
                    onClose({ index, position: Math.max(0, status?.positionMillis || 0), isPlaying: !!status?.isPlaying })
                } catch {
                    onClose({ index, position: uiPosition || 0, isPlaying: playback.isPlaying })
                }
            }}
        >
            <View style={{ flex: 1, backgroundColor: '#000' }}>
                {/* Top bar */}
                <View
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 64,
                        paddingTop: 24,
                        paddingHorizontal: 16,
                        zIndex: 20,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <TouchableOpacity
                        onPress={async () => {
                            try {
                                const status = await videoRef.current?.getStatusAsync?.()
                                onClose({ index, position: Math.max(0, status?.positionMillis || 0), isPlaying: !!status?.isPlaying })
                            } catch {
                                onClose({ index, position: uiPosition || 0, isPlaying: playback.isPlaying })
                            }
                        }}
                        hitSlop={{ top: 12, left: 12, right: 12, bottom: 12 }}
                    >
                        <Feather name='x' size={28} color='#fff' />
                    </TouchableOpacity>
                    <Text style={{ color: '#fff' }}>
                        {index + 1}/{Array.isArray(medias) ? medias.length : 0}
                    </Text>
                    <View style={{ width: 28 }} />
                </View>

                {/* Center content */}
                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                    {type === 'video' ? (
                        <VideoPlayerAV
                            uri={uri}
                            onVideoRef={(ref) => {
                                videoRef.current = ref
                            }}
                            autoplay={autoplay}
                            isVisible={true}
                            post_id={instanceId}
                            isPodcast={false}
                            contentSeen={() => {}}
                            path={path}
                            containerWidth={Math.min(screenW, (screenH * 9) / 16)}
                            onReady={() => {}}
                            onPlaybackStatusUpdateExternal={handlePlayStatus}
                            onNaturalSize={() => {}}
                            waitForNaturalSize={false}
                            controlsMode='minimal'
                            allowAutoplayAfterUserGesture={true}
                            onUserPlayIntent={() => {}}
                        />
                    ) : type === 'image' ? (
                        <View style={{ width: screenW, paddingHorizontal: 24 }}>
                            <ImageWithZoom uri={uri} contentSeen={() => {}} path={path} containerWidth={screenW - 48} onReady={() => {}} />
                        </View>
                    ) : (
                        <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ color: '#fff' }}>Contenido no soportado en fullscreen</Text>
                        </View>
                    )}
                </View>

                {/* Navigation arrows */}
                {hasPrev && (
                    <TouchableOpacity
                        onPress={() => {
                            cancelUpNext()
                            setIndex((i) => Math.max(0, i - 1))
                        }}
                        style={{
                            position: 'absolute',
                            left: 16,
                            top: '50%',
                            transform: [{ translateY: -20 }],
                            backgroundColor: 'rgba(0,0,0,0.45)',
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 20,
                        }}
                    >
                        <Ionicons name='chevron-back' size={26} color='#fff' />
                    </TouchableOpacity>
                )}
                {hasNext && (
                    <TouchableOpacity
                        onPress={() => {
                            cancelUpNext()
                            setIndex((i) => i + 1)
                        }}
                        style={{
                            position: 'absolute',
                            right: 16,
                            top: '50%',
                            transform: [{ translateY: -20 }],
                            backgroundColor: 'rgba(0,0,0,0.45)',
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 20,
                        }}
                    >
                        <Ionicons name='chevron-forward' size={26} color='#fff' />
                    </TouchableOpacity>
                )}

                {/* Custom controls bar */}
                {type === 'video' && (
                    <View
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            paddingHorizontal: 16,
                            paddingBottom: 14,
                            paddingTop: 10,
                            backgroundColor: 'rgba(0,0,0,0.35)',
                        }}
                    >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <TouchableOpacity
                                onPress={async () => {
                                    try {
                                        if (playback.isPlaying) await videoRef.current?.pauseAsync()
                                        else await videoRef.current?.playAsync()
                                    } catch {}
                                }}
                                style={{ padding: 8, marginRight: 8 }}
                                hitSlop={{ top: 6, left: 6, right: 6, bottom: 6 }}
                            >
                                {playback.isPlaying ? (
                                    <Feather name='pause' size={18} color='#fff' />
                                ) : (
                                    <Feather name='play' size={18} color='#fff' />
                                )}
                            </TouchableOpacity>
                            {/* Progress (web: custom bar + hover; native: Slider con scrubbing continuo visual) */}
                            {Platform.OS === 'web' ? (
                                <View style={{ flex: 1, height: 28, justifyContent: 'center' }}>
                                    <View
                                        ref={progressBarRef}
                                        style={{
                                            height: 6,
                                            backgroundColor: 'rgba(255,255,255,0.25)',
                                            borderRadius: 3,
                                            position: 'relative',
                                            cursor: 'pointer',
                                        }}
                                        onLayout={(e) => {
                                            progressBarWidthRef.current = e?.nativeEvent?.layout?.width || 0
                                        }}
                                        onMouseMove={(e) => {
                                            try {
                                                const rect = progressBarRef.current?.getBoundingClientRect?.()
                                                const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                                const w = rect?.width
                                                if (w > 0 && typeof pageX === 'number') setHoverPct(Math.max(0, Math.min(1, (pageX - rect.left) / w)))
                                            } catch {}
                                        }}
                                        onMouseLeave={() => setHoverPct(null)}
                                        onStartShouldSetResponder={() => true}
                                        onResponderGrant={async (e) => {
                                            try {
                                                // Interacción del usuario: cancelar overlay Up Next
                                                cancelUpNext()
                                                const rect = progressBarRef.current?.getBoundingClientRect?.()
                                                const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                                const w = rect?.width || progressBarWidthRef.current || 0
                                                if (!w || !playback.duration || typeof pageX !== 'number') return
                                                wasPlayingRef.current = !!playback.isPlaying
                                                if (wasPlayingRef.current) await videoRef.current?.pauseAsync()
                                                setIsSeeking(true)
                                                const pct = Math.max(0, Math.min(1, (pageX - rect.left) / w))
                                                setTempPosition(pct * playback.duration)
                                            } catch {}
                                        }}
                                        onResponderMove={(e) => {
                                            try {
                                                const rect = progressBarRef.current?.getBoundingClientRect?.()
                                                const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                                const w = rect?.width || progressBarWidthRef.current || 0
                                                if (!w || !playback.duration || typeof pageX !== 'number') return
                                                const pct = Math.max(0, Math.min(1, (pageX - rect.left) / w))
                                                setTempPosition(pct * playback.duration)
                                            } catch {}
                                        }}
                                        onResponderRelease={async (e) => {
                                            try {
                                                const rect = progressBarRef.current?.getBoundingClientRect?.()
                                                const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                                const w = rect?.width || progressBarWidthRef.current || 0
                                                if (w && typeof pageX === 'number' && playback.duration > 0) {
                                                    const p = Math.max(0, Math.min(1, (pageX - rect.left) / w))
                                                    const newPos = Math.round((playback.duration || 0) * p)
                                                    if (Number.isFinite(newPos)) await videoRef.current?.setPositionAsync(newPos)
                                                }
                                            } catch {}
                                            setIsSeeking(false)
                                            setTempPosition(null)
                                            try {
                                                if (wasPlayingRef.current) await videoRef.current?.playAsync()
                                            } catch {}
                                        }}
                                    >
                                        {/* played */}
                                        <View style={{ height: 6, width: `${progressPct * 100}%`, backgroundColor: '#fff', borderRadius: 3 }} />
                                        {/* hover preview marker (web) */}
                                        {typeof hoverPct === 'number' && hoverPct >= 0 && hoverPct <= 1 && (
                                            <View
                                                style={{
                                                    position: 'absolute',
                                                    left: `${hoverPct * 100}%`,
                                                    top: -20,
                                                    transform: [{ translateX: -12 }],
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <View
                                                    style={{
                                                        backgroundColor: 'rgba(0,0,0,0.75)',
                                                        paddingHorizontal: 6,
                                                        paddingVertical: 2,
                                                        borderRadius: 4,
                                                    }}
                                                >
                                                    <Text style={{ color: '#fff', fontSize: 10 }}>{toTime((playback.duration || 0) * hoverPct)}</Text>
                                                </View>
                                                <View style={{ width: 2, height: 14, backgroundColor: '#fff', marginTop: 4 }} />
                                            </View>
                                        )}
                                    </View>
                                </View>
                            ) : (
                                <View style={{ flex: 1, paddingHorizontal: 4 }}>
                                    <Slider
                                        value={progressPct}
                                        minimumValue={0}
                                        maximumValue={1}
                                        minimumTrackTintColor={'#fff'}
                                        maximumTrackTintColor={'rgba(255,255,255,0.25)'}
                                        thumbTintColor={'#fff'}
                                        onSlidingStart={() => {
                                            try {
                                                // Interacción del usuario: cancelar overlay Up Next
                                                cancelUpNext()
                                                wasPlayingRef.current = playback.isPlaying
                                                if (playback.isPlaying) videoRef.current?.pauseAsync()
                                                setIsSeeking(true)
                                            } catch {}
                                        }}
                                        onValueChange={(val) => {
                                            try {
                                                if (playback.duration > 0) setTempPosition(Math.max(0, Math.min(1, val)) * playback.duration)
                                            } catch {}
                                        }}
                                        onSlidingComplete={async (val) => {
                                            try {
                                                const pct = Math.max(0, Math.min(1, val))
                                                const newPos = Math.round((playback.duration || 0) * pct)
                                                if (Number.isFinite(newPos)) await videoRef.current?.setPositionAsync(newPos)
                                                setIsSeeking(false)
                                                setTempPosition(null)
                                                if (wasPlayingRef.current) await videoRef.current?.playAsync()
                                            } catch {
                                                setIsSeeking(false)
                                                setTempPosition(null)
                                            }
                                        }}
                                    />
                                </View>
                            )}
                            <Text style={{ color: '#fff', fontSize: 12, marginLeft: 8 }}>
                                {toTime(uiPosition)} / {toTime(playback.duration)}
                            </Text>
                            {/* Volume controls */}
                            <TouchableOpacity
                                onPress={async () => {
                                    try {
                                        const next = playback.volume > 0 ? 0 : 1
                                        await videoRef.current?.setStatusAsync({ volume: next })
                                        setPlayback((p) => ({ ...p, volume: next }))
                                    } catch {}
                                }}
                                style={{ padding: 8, marginLeft: 8 }}
                                hitSlop={{ top: 6, left: 6, right: 6, bottom: 6 }}
                            >
                                <Feather name={playback.volume > 0 ? 'volume-2' : 'volume-x'} size={18} color='#fff' />
                            </TouchableOpacity>
                            <View style={{ width: 80, marginLeft: 4, height: 24, justifyContent: 'center', position: 'relative' }}>
                                <View ref={volumeBarRef} style={{ height: 4, backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 2 }}>
                                    <View
                                        style={{
                                            height: 4,
                                            width: `${Math.max(0, Math.min(1, playback.volume || 0)) * 100}%`,
                                            backgroundColor: '#fff',
                                            borderRadius: 2,
                                        }}
                                    />
                                </View>
                                <TouchableOpacity
                                    onPress={async (e) => {
                                        try {
                                            const rect = volumeBarRef.current?.getBoundingClientRect?.()
                                            const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                            const w = rect?.width || e?.currentTarget?.clientWidth || 0
                                            const x = typeof pageX === 'number' && rect ? pageX - rect.left : e?.nativeEvent?.locationX || 0
                                            if (w > 0) {
                                                const pct = Math.max(0, Math.min(1, x / w))
                                                await videoRef.current?.setStatusAsync({ volume: pct })
                                                setPlayback((p) => ({ ...p, volume: pct }))
                                            }
                                        } catch {}
                                    }}
                                    activeOpacity={1}
                                    style={{ ...StyleSheet.absoluteFillObject }}
                                />
                            </View>
                        </View>
                    </View>
                )}

                {/* Up Next overlay */}
                {hasNext && upNextActiveRef.current && typeof upNextCountdown === 'number' && upNextCountdown > 0 && (
                    // Overlay no bloqueante: fondo con pointerEvents="none" y panel central interactivo
                    <View
                        style={{ ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', zIndex: 30 }}
                        pointerEvents='box-none'
                    >
                        {/* Fondo oscurecido que no intercepta eventos */}
                        <View style={{ ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.4)' }} pointerEvents='none' />
                        {/* Panel interactivo */}
                        <View
                            style={{
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                paddingHorizontal: 20,
                                paddingVertical: 16,
                                borderRadius: 12,
                                alignItems: 'center',
                                maxWidth: 280,
                            }}
                            pointerEvents='auto'
                        >
                            <Text style={{ color: '#fff', fontSize: 14, marginBottom: 6 }}>Reproduciendo siguiente en</Text>
                            <Text style={{ color: '#fff', fontSize: 42, fontWeight: '800' }}>{upNextCountdown}</Text>
                            <TouchableOpacity
                                onPress={() => {
                                    endHandledRef.current = true
                                    cancelUpNext()
                                    setIndex((i) => i + 1)
                                }}
                                activeOpacity={0.9}
                                style={{ marginTop: 12, backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 24 }}
                            >
                                <Text style={{ color: '#000', fontWeight: '700' }}>Reproducir siguiente</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                )}
            </View>
        </Modal>
    )
}
